import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  IconButton,
  LinearProgress,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  DeviceThermostat,
  Water,
  Co2,
  WbSunny,
  Grass,
  Science,
  ElectricBolt,
  Refresh,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Sensor, SensorData, Farm } from '../types';
import { sensorApi, farmApi } from '../services/api';
import { websocketService } from '../services/websocket';

interface DashboardSensorData {
  id: string;
  name: string;
  value: number;
  unit: string;
  trend: 'up' | 'down';
  trendValue: number;
  icon: React.ReactNode;
  color: string;
  data: Array<{ time: string; value: number }>;
}

interface DeviceStatus {
  id: string;
  name: string;
  type: string;
  status: 'on' | 'off';
  icon: React.ReactNode;
}

const DashboardPage: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeDevices, setActiveDevices] = useState(0);
  const [alerts, setAlerts] = useState(0);
  const [dataUpdate, setDataUpdate] = useState('실시간');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [sensorDataMap, setSensorDataMap] = useState<Map<number, SensorData[]>>(new Map());

  // 실제 센서 데이터를 UI용 형태로 변환
  const [sensorData, setSensorData] = useState<DashboardSensorData[]>([]);

  // 디바이스 상태
  const [devices] = useState<DeviceStatus[]>([
    { id: 'pump', name: '펌프', type: '급/배액', status: 'off', icon: <Water /> },
    { id: 'led', name: 'LED', type: '생장등', status: 'off', icon: <WbSunny /> },
    { id: 'fan', name: '팬', type: '환기', status: 'off', icon: <Refresh /> },
    { id: 'cooler', name: '쿨러', type: '냉각', status: 'on', icon: <DeviceThermostat /> },
  ]);

  // 센서 타입별 아이콘 매핑
  const getSensorIcon = (sensorType: string) => {
    switch (sensorType.toLowerCase()) {
      case 'temperature': return <DeviceThermostat />;
      case 'humidity': return <Water />;
      case 'co2': return <Co2 />;
      case 'light': return <WbSunny />;
      case 'soil_moisture': return <Grass />;
      case 'ph': return <Science />;
      case 'ec': return <ElectricBolt />;
      default: return <DeviceThermostat />;
    }
  };

  // 센서 타입별 단위 매핑
  const getSensorUnit = (sensorType: string) => {
    switch (sensorType.toLowerCase()) {
      case 'temperature': return '°C';
      case 'humidity': return '%';
      case 'co2': return 'ppm';
      case 'light': return 'lux';
      case 'soil_moisture': return '%';
      case 'ph': return 'pH';
      case 'ec': return 'EC';
      default: return '';
    }
  };

  // 데이터 로드
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 농장 목록 조회
      const farmsResponse = await farmApi.getAll();
      setFarms(farmsResponse.data);

      // 센서 목록 조회
      const sensorsResponse = await sensorApi.getAll();
      setSensors(sensorsResponse.data);

      // 각 센서의 최신 데이터 조회
      const sensorDataPromises = sensorsResponse.data.map(async (sensor) => {
        try {
          const dataResponse = await sensorApi.getData(sensor.id);
          return { sensorId: sensor.id, data: dataResponse.data };
        } catch (error) {
          console.error(`센서 ${sensor.id} 데이터 조회 실패:`, error);
          return { sensorId: sensor.id, data: [] };
        }
      });

      const sensorDataResults = await Promise.all(sensorDataPromises);
      const newSensorDataMap = new Map();
      sensorDataResults.forEach(({ sensorId, data }) => {
        newSensorDataMap.set(sensorId, data.slice(-10)); // 최근 10개 데이터만 저장
      });
      setSensorDataMap(newSensorDataMap);

      // UI용 센서 데이터 생성
      const dashboardSensorData: DashboardSensorData[] = sensorsResponse.data.map((sensor) => {
        const recentData = newSensorDataMap.get(sensor.id) || [];
        const latestData = recentData[recentData.length - 1];
        const previousData = recentData[recentData.length - 2];

        const value = latestData?.value || 0;
        const previousValue = previousData?.value || value;
        const trendValue = value - previousValue;
        const trend = trendValue >= 0 ? 'up' : 'down';

        const chartData = recentData.map((data: any, index: number) => ({
          time: new Date(data.timestamp).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
          value: data.value,
        }));

        return {
          id: sensor.id.toString(),
          name: sensor.name,
          value: value,
          unit: latestData?.unit || getSensorUnit(sensor.sensor_type),
          trend,
          trendValue: Math.abs(trendValue),
          icon: getSensorIcon(sensor.sensor_type),
          color: '#FF6B9D',
          data: chartData,
        };
      });

      setSensorData(dashboardSensorData);

      // 활성 센서 수 계산
      const activeSensorCount = sensorsResponse.data.filter(s => s.is_active).length;
      setActiveDevices(activeSensorCount);

    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 데이터 새로고침
  const handleRefresh = () => {
    loadDashboardData();
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // 초기 데이터 로드
    loadDashboardData();

    // 30초마다 데이터 자동 갱신
    const dataRefreshInterval = setInterval(() => {
      loadDashboardData();
    }, 30000);

    return () => {
      clearInterval(timer);
      clearInterval(dataRefreshInterval);
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>데이터를 불러오는 중...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleRefresh}>
          다시 시도
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                기능 중인 장치
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {activeDevices}
                <Typography component="span" variant="body1" sx={{ ml: 1, color: 'text.secondary' }}>
                  / {sensors.length}
                </Typography>
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                활성 센서 수
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                이상 알림
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {alerts}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                건
              </Typography>
              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                실시간 초과/미달 상황 감지
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                현재 환경
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {sensorData.slice(0, 3).map((sensor) => (
                  <Typography key={sensor.id} variant="h6">
                    {sensor.value.toFixed(1)}
                  </Typography>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {sensorData.slice(0, 3).map((sensor) => `${sensor.name}(${sensor.unit})`).join(' ')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                데이터 수집
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {dataUpdate}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                실시간 수집
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sensor Data Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mr: 2 }}>
            실시간 센서 상태
          </Typography>
          <IconButton size="small" sx={{ color: 'primary.main' }} onClick={handleRefresh}>
            <Refresh />
          </IconButton>
        </Box>

        <Grid container spacing={3}>
          {sensorData.map((sensor) => (
            <Grid item xs={12} md={6} key={sensor.id}>
              <Card sx={{ borderRadius: 3, height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: 'primary.light',
                        color: 'white',
                        mr: 2,
                      }}
                    >
                      {sensor.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {sensor.name} {sensor.unit}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mr: 1 }}>
                          {sensor.value}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {sensor.trend === 'up' ? (
                            <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <TrendingDown sx={{ fontSize: 16, color: 'info.main' }} />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              color: sensor.trend === 'up' ? 'success.main' : 'info.main',
                              ml: 0.5,
                            }}
                          >
                            {sensor.trend === 'up' ? '+' : ''}{sensor.trendValue} {sensor.unit}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Mini Chart */}
                  <Box sx={{ height: 120, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sensor.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="time"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#999' }}
                        />
                        <YAxis hide />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={sensor.color}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Device Control Section */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          주요 장치 상태
        </Typography>
        <Grid container spacing={2}>
          {devices.map((device) => (
            <Grid item xs={12} sm={6} md={3} key={device.id}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  textAlign: 'center',
                  bgcolor: device.status === 'on' ? 'success.light' : 'grey.100',
                  color: device.status === 'on' ? 'white' : 'text.primary',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: device.status === 'on' ? 'rgba(255,255,255,0.2)' : 'grey.200',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  {device.icon}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {device.name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {device.type}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={device.status === 'on' ? 'ON' : 'OFF'}
                    size="small"
                    sx={{
                      bgcolor: device.status === 'on' ? 'success.main' : 'grey.400',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default DashboardPage;